interface CartProductNotificationState {
    isVisible: boolean
    productName?: string
}

/**
 * Composable for managing cart product-added notifications
 * Uses Nuxt's useState for SSR-safe state management
 *
 * @category Cart & Checkout
 * @returns Cart product notification functions and state
 */
export function useCartProductNotification() {
    // SSR-safe state using Nuxt's useState - auto-isolates per request
    const state = useState<CartProductNotificationState>('cart-product-notification', () => ({
        isVisible: false,
        productName: undefined
    }))

    // Store timeout in a ref for proper cleanup
    const hideTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

    /**
     * Show a notification that a product was added to the cart
     *
     * @param productName - Optional product name to display in notification
     * @param duration - Duration in milliseconds to show the notification (default: 2000ms)
     */
    const showProductAdded = (productName?: string, duration: number = 2000) => {
        if (hideTimeout.value) {
            clearTimeout(hideTimeout.value)
        }

        state.value.isVisible = true
        state.value.productName = productName

        hideTimeout.value = setTimeout(() => {
            state.value.isVisible = false
            state.value.productName = undefined
        }, duration)
    }

    // Hide notification immediately
    const hide = () => {
        if (hideTimeout.value) {
            clearTimeout(hideTimeout.value)
        }
        state.value.isVisible = false
        state.value.productName = undefined
    }

    return {
        showProductAdded,
        isVisible: computed(() => state.value.isVisible),
        productName: computed(() => state.value.productName),
        hide
    }
}
